import { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';
import { Toaster } from 'sonner';

import Navigation from '@/components/layout/Navigation';
import Footer from '@/components/layout/Footer';
import ScrollToTop from '@/components/layout/ScrollToTop';
import FloatingScrollControls from '@/components/layout/FloatingScrollControls';

import Home from '@/pages/Home';
import About from '@/pages/About';
import Companies from '@/pages/Companies';
import Speaking from '@/pages/Speaking';
import Media from '@/pages/Media';
import Resources from '@/pages/Resources';
import WorkWithMe from '@/pages/WorkWithMe';
import Contact from '@/pages/Contact';
import Journal from '@/pages/Journal';
import JournalArticle from '@/pages/JournalArticle';
import Now from '@/pages/Now';
import Partners from '@/pages/Partners';
import Credentials from '@/pages/Credentials';
import RefundPolicy from '@/pages/RefundPolicy';
import Terms from '@/pages/Terms';
import NoRefundPolicy from '@/pages/NoRefundPolicy';
import ReschedulingCancellationPolicy from '@/pages/ReschedulingCancellationPolicy';
import StrategyIntensive from '@/pages/StrategyIntensive';
import { StrategyIntensivePay, StrategyIntensiveSchedule, StrategyIntensivePrep } from '@/pages/StrategyIntensiveFlow';
import FoundersList from '@/pages/FoundersList';
import FoundersListConfirmed from '@/pages/FoundersListConfirmed';
import Unsubscribe from '@/pages/Unsubscribe';
import FounderScorecard from '@/pages/FounderScorecard';
import DigitalProducts from '@/pages/DigitalProducts';
import DigitalProductDetail from '@/pages/DigitalProductDetail';
import DigitalProductThankYou from '@/pages/DigitalProductThankYou';
import DigitalDownload from '@/pages/DigitalDownload';
import FounderLogin from '@/pages/founder/FounderLogin';
import FounderVerify from '@/pages/founder/FounderVerify';
import FounderWorkspace from '@/pages/founder/FounderWorkspace';
import BlueprintThankYou from '@/pages/founder/BlueprintThankYou';
import BlueprintSalesPage from '@/pages/founder/BlueprintSalesPage';
import StageStudio from '@/pages/founder/StageStudio';
import BlueprintDocument from '@/pages/founder/BlueprintDocument';

// Admin
import { AdminAuthProvider } from '@/admin/AdminAuthContext';
import AdminLogin from '@/admin/AdminLogin';
import AdminLayout from '@/admin/AdminLayout';
import Dashboard from '@/admin/Dashboard';
import SubmissionsView from '@/admin/SubmissionsView';
import AllSubmissions from '@/admin/AllSubmissions';
import Editions from '@/admin/Editions';
import Analytics from '@/admin/Analytics';
import Campaigns from '@/admin/Campaigns';
import WelcomeSequence from '@/admin/WelcomeSequence';
import StrategyIntensiveAdmin from '@/admin/StrategyIntensiveAdmin';
import SpeakingEngagementsAdmin from '@/admin/SpeakingEngagementsAdmin';
import ScorecardAdmin from '@/admin/ScorecardAdmin';
import DigitalProductsAdmin from '@/admin/DigitalProductsAdmin';
import FounderBlueprintAdmin from '@/admin/FounderBlueprintAdmin';
import { SpeakingAgreement, SpeakingPay, SpeakingPaid } from '@/pages/SpeakingFlow';

import analytics from '@/lib/analytics';

function PublicShell() {
  return (
    <>
      <Navigation />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/companies" element={<Companies />} />
          <Route path="/speaking" element={<Speaking />} />
          <Route path="/media" element={<Media />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/digital-products/thank-you" element={<DigitalProductThankYou />} />
          <Route path="/resources/digital-products/founder-blueprint-builder" element={<BlueprintSalesPage />} />
          <Route path="/resources/:section" element={<DigitalProducts />} />
          <Route path="/resources/:section/:slug" element={<DigitalProductDetail />} />
          <Route path="/download/:code" element={<DigitalDownload />} />
          <Route path="/work-with-me" element={<WorkWithMe />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:slug" element={<JournalArticle />} />
          <Route path="/now" element={<Now />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/executive" element={<Partners />} />
          <Route path="/speaking-kit" element={<Partners />} />
          <Route path="/credentials" element={<Credentials />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/no-refund-policy" element={<NoRefundPolicy />} />
          <Route path="/rescheduling-cancellation-policy" element={<ReschedulingCancellationPolicy />} />
          <Route path="/strategy-intensive" element={<StrategyIntensive />} />
          <Route path="/strategy-intensive/pay/:token" element={<StrategyIntensivePay />} />
          <Route path="/strategy-intensive/schedule/:token" element={<StrategyIntensiveSchedule />} />
          <Route path="/strategy-intensive/prep/:token" element={<StrategyIntensivePrep />} />
          <Route path="/speaking/agreement/:token" element={<SpeakingAgreement />} />
          <Route path="/speaking/pay/:token" element={<SpeakingPay />} />
          <Route path="/speaking/paid/:token" element={<SpeakingPaid />} />
          <Route path="/founders-list" element={<FoundersList />} />
          <Route path="/founders-list/confirmed" element={<FoundersListConfirmed />} />
          <Route path="/founder-scorecard" element={<FounderScorecard />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/founder/login" element={<FounderLogin />} />
          <Route path="/founder/verify" element={<FounderVerify />} />
          <Route path="/founder/workspace" element={<FounderWorkspace />} />
          <Route path="/founder/blueprint/:bid/:stage" element={<StageStudio />} />
          <Route path="/founder/blueprint/:bid" element={<BlueprintDocument />} />
          <Route path="/founder-blueprint-builder/thank-you" element={<BlueprintThankYou />} />
        </Routes>
      </main>
      <Footer />
      <FloatingScrollControls />
    </>
  );
}

function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/founder-command');

  // Fire GA4 page_view on every route change (excluding admin routes)
  useEffect(() => {
    if (isAdmin) return;
    analytics.pageView(location.pathname + location.search);
  }, [location.pathname, location.search, isAdmin]);

  useEffect(() => {
    // Smooth scroll only on public site — admin dashboard uses native scroll
    if (isAdmin) return;
    const lenis = new Lenis({
      duration: 1.35,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    // Expose instance so route changes (ScrollToTop) can reset position
    // via Lenis's own scrollTo (window.scrollTo is intercepted by the lib).
    window.__lenis = lenis;
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    return () => {
      if (window.__lenis === lenis) delete window.__lenis;
      lenis.destroy();
    };
  }, [isAdmin]);

  return (
    <Routes>
      {/* Admin routes — isolated shell, not linked in public nav */}
      <Route
        path="/founder-command/*"
        element={
          <AdminAuthProvider>
            <Routes>
              <Route path="login" element={<AdminLogin />} />
              <Route element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="contacts" element={<SubmissionsView section="contacts" />} />
                <Route path="speaking" element={<SpeakingEngagementsAdmin />} />
                <Route path="newsletter" element={<SubmissionsView section="newsletter" />} />
                <Route path="waitlist" element={<SubmissionsView section="waitlist" />} />
                <Route path="submissions" element={<AllSubmissions />} />
                <Route path="editions" element={<Editions />} />
                <Route path="welcome-sequence" element={<WelcomeSequence />} />
                <Route path="strategy-intensive" element={<StrategyIntensiveAdmin />} />
                <Route path="scorecard" element={<ScorecardAdmin />} />
                <Route path="digital-products" element={<DigitalProductsAdmin />} />
                <Route path="founder-blueprint" element={<FounderBlueprintAdmin />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="campaigns" element={<Campaigns />} />
              </Route>
            </Routes>
          </AdminAuthProvider>
        }
      />
      {/* Everything else → public site */}
      <Route path="/*" element={<PublicShell />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppShell />
        <Toaster position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
